import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarPlus, MapPin, ArrowRight } from "lucide-react";
import { calendarLinks, directionsLink } from "@/lib/calendar";
import { ShareButton } from "@/components/ShareButton";

function Confetti() {
  const pieces = Array.from({ length: 40 });
  const colors = ["#C59B27", "#E7C766", "#F7EFD4", "#2C201A", "#E8DEC8"];
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      {pieces.map((_, i) => (
        <motion.span key={i}
          initial={{ y: -40, x: `${Math.random() * 100}vw`, opacity: 1, rotate: 0 }}
          animate={{ y: "110vh", rotate: 360 * (Math.random() > 0.5 ? 1 : -1) }}
          transition={{ duration: 2.4 + Math.random() * 1.6, delay: Math.random() * 0.6, ease: "easeIn" }}
          style={{ background: colors[i % colors.length], width: 8, height: 12, position: "absolute", borderRadius: 2 }} />
      ))}
    </div>
  );
}

export function Confirmation({ rsvp, event }) {
  const nav = useNavigate();
  const [cal, setCal] = useState(false);
  const declined = rsvp.status === "declined";
  const address = rsvp.event_address || event?.address;
  const links = calendarLinks({
    title: "Mike & Sam's Friendsgiving",
    description: "You're on the list! See you at Friendsgiving. 🥂",
    location: address || "Westlake, FL",
    startISO: event?.event_datetime_iso || "2026-11-14T14:00:00-05:00",
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (declined) {
    return (
      <div className="min-h-screen bg-cream grid place-items-center px-5">
        <div className="text-center max-w-md animate-scale-in">
          <div className="text-5xl mb-4">💛</div>
          <h1 className="font-serif text-4xl text-espresso">We'll miss you, {rsvp.primary_name.split(" ")[0]}</h1>
          <p className="text-inkSoft mt-3">Thanks for letting us know. If your plans change, you can always come back and update your RSVP.</p>
          <button onClick={() => nav("/")} className="mt-8 gold-gradient text-espressoDeep font-bold px-8 py-4 rounded-full shadow-gold" data-testid="declined-home">Back to Invitation</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      <Confetti />
      <div className="relative z-10 max-w-lg mx-auto px-5 py-16 text-center">
        <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 12 }} className="text-6xl mb-4">
          🥂
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-serif text-4xl sm:text-5xl text-espresso" data-testid="confirmation-title">
          You're on the list!
        </motion.h1>
        <p className="text-inkSoft mt-3 text-lg">We can't wait to celebrate Friendsgiving with you.</p>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-8 rounded-3xl bg-white border border-hair shadow-lift p-8 text-left">
          <div className="flex justify-between py-2"><span className="text-eyebrow text-inkMuted">Date</span><span className="font-serif text-lg text-espresso">Nov 14, 2026</span></div>
          <div className="flex justify-between py-2 border-t border-hair"><span className="text-eyebrow text-inkMuted">Time</span><span className="font-serif text-lg text-espresso">2:00 PM EST</span></div>
          <div className="flex justify-between py-2 border-t border-hair"><span className="text-eyebrow text-inkMuted">Party</span><span className="font-serif text-lg text-espresso">Party of {rsvp.party_size}</span></div>
          {rsvp.dish_active && rsvp.dish && (
            <div className="flex justify-between py-2 border-t border-hair"><span className="text-eyebrow text-inkMuted">Bringing</span><span className="font-serif text-lg text-espresso">{rsvp.dish.name}</span></div>
          )}
        </motion.div>

        <div className="mt-8 space-y-3">
          <div className="relative">
            <button onClick={() => setCal(!cal)} className="w-full flex items-center justify-center gap-2 gold-gradient text-espressoDeep font-bold py-4 rounded-full shadow-gold active:scale-95 transition" data-testid="add-to-calendar">
              <CalendarPlus size={20} /> Add to Calendar
            </button>
            {cal && (
              <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl border border-hair shadow-lift overflow-hidden z-20" data-testid="calendar-menu">
                <a href={links.google} target="_blank" rel="noreferrer" className="block px-5 py-3 text-left hover:bg-cream2 text-espresso font-medium" data-testid="cal-google">Google Calendar</a>
                <a href={links.ics} download="friendsgiving.ics" className="block px-5 py-3 text-left hover:bg-cream2 text-espresso font-medium border-t border-hair" data-testid="cal-apple">Apple Calendar</a>
                <a href={links.outlook} target="_blank" rel="noreferrer" className="block px-5 py-3 text-left hover:bg-cream2 text-espresso font-medium border-t border-hair" data-testid="cal-outlook">Outlook</a>
              </div>
            )}
          </div>
          {address && (
            <a href={directionsLink(address)} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 bg-espresso text-cream font-semibold py-4 rounded-full active:scale-95 transition" data-testid="conf-directions">
              <MapPin size={20} /> Get Directions
            </a>
          )}
          <button onClick={() => nav("/my-rsvp")} className="w-full flex items-center justify-center gap-2 border border-hair bg-white text-espresso font-semibold py-4 rounded-full active:scale-95 transition" data-testid="conf-view-rsvp">
            View My RSVP <ArrowRight size={18} />
          </button>
        </div>

        <div className="mt-8 rounded-2xl bg-cream2 border border-hair p-4">
          <p className="text-eyebrow text-inkMuted mb-1">📱 Confirmation Sent</p>
          <p className="text-sm text-inkSoft">"{rsvp.primary_name.split(" ")[0]}, you're officially on the list for Mike & Sam's Friendsgiving! 🥂 We'll see you November 14 at 2:00 PM."</p>
        </div>

        <div className="mt-8 flex justify-center"><ShareButton label="Invite Friends" /></div>
      </div>
    </div>
  );
}
